import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { NAV as DEFAULT_NAV } from '../../data/mock';

export default function AdminMenu() {
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    api.adminListMenu().then(setItems).catch(() => toast.error('Menü yüklenemedi'));
    api.adminListPages().then(setPages).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const tree = buildTree(items, null);

  const addChild = (parentId) => {
    const siblings = items.filter((x) => x.parent_id === parentId);
    api.adminCreateMenu({
      label: 'Yeni Menü Öğesi',
      url: '/',
      parent_id: parentId,
      sort_order: siblings.length,
      open_in_new_tab: false,
    }).then((it) => {
      setItems((prev) => prev.concat([it]));
      toast.success('Menü öğesi eklendi');
    }).catch(() => toast.error('Eklenemedi'));
  };

  const updateItem = (id, patch) => {
    const existing = items.find((x) => x.id === id);
    if (!existing) return;
    const merged = Object.assign({}, existing, patch);
    api.adminUpdateMenu(id, {
      label: merged.label,
      url: merged.url,
      parent_id: merged.parent_id,
      sort_order: merged.sort_order,
      open_in_new_tab: !!merged.open_in_new_tab,
    }).then((updated) => {
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
    }).catch(() => toast.error('Güncellenemedi'));
  };

  const deleteItem = (id) => {
    if (!window.confirm('Bu öğe ve tüm alt öğeleri silinecek. Emin misiniz?')) return;
    api.adminDeleteMenu(id).then(() => { load(); toast.success('Silindi'); })
      .catch(() => toast.error('Silinemedi'));
  };

  const move = (id, direction) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;
    const siblings = items.filter((s) => s.parent_id === it.parent_id).sort(sortBySortOrder);
    const idx = siblings.findIndex((s) => s.id === id);
    const j = direction === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= siblings.length) return;
    const a = siblings[idx];
    const b = siblings[j];
    api.adminReorderMenu([
      { id: a.id, parent_id: a.parent_id, sort_order: b.sort_order },
      { id: b.id, parent_id: b.parent_id, sort_order: a.sort_order },
    ]).then(load).catch(() => toast.error('Sıralama değiştirilemedi'));
  };

  const seedFromLive = async () => {
    if (!window.confirm('Şu anda sitede kullanılan varsayılan menü DB\'ye kopyalanacak, sonra istediğiniz gibi düzenleyebilirsiniz. Devam edilsin mi?')) return;
    setSeeding(true);
    try {
      let created = 0;
      for (let i = 0; i < DEFAULT_NAV.length; i++) {
        const top = DEFAULT_NAV[i];
        const parent = await api.adminCreateMenu({
          label: top.label,
          url: top.path || '/',
          parent_id: null,
          sort_order: i,
          open_in_new_tab: false,
        });
        created += 1;
        if (Array.isArray(top.submenu)) {
          for (let j = 0; j < top.submenu.length; j++) {
            const sub = top.submenu[j];
            await api.adminCreateMenu({
              label: sub.label,
              url: sub.path || '/',
              parent_id: parent.id,
              sort_order: j,
              open_in_new_tab: false,
            });
            created += 1;
          }
        }
      }
      await load();
      toast.success(created + ' menü öğesi içeri aktarıldı');
    } catch {
      toast.error('İçeri aktarma başarısız');
    } finally { setSeeding(false); }
  };

  const flat = flatten(tree, 0);

  return (
    <div className="p-4 md:p-8" data-testid="admin-menu-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Menü</div>
          <h1 className="font-display text-2xl md:text-3xl text-ink-600 mt-1">Ana Navigasyon Menüsü</h1>
          <p className="text-ink-400 text-sm mt-1">3 seviyeye kadar ana menü / alt menü / alt-alt menü. Boş bırakırsanız kod içindeki varsayılan menü kullanılır.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {items.length === 0 && (
            <button onClick={seedFromLive} disabled={seeding} data-testid="seed-live-menu" className="border border-gold bg-gold/10 text-ink-600 rounded-full px-5 h-11 text-sm font-semibold hover:bg-gold/20 disabled:opacity-60">
              ⚡ Sitedeki Aktif Menüyü Yükle
            </button>
          )}
          <button onClick={() => addChild(null)} data-testid="add-top-level" className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500">
            + Ana Menü Öğesi Ekle
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-ink-200 p-10 text-center">
          <div className="text-ink-500 text-sm max-w-md mx-auto">Henüz DB&apos;de menü öğesi yok. Şu anda site kodda tanımlı varsayılan menüyü kullanıyor. Aşağıdaki butonla mevcut menüyü buraya kopyalayabilir, sonra düzenleyebilirsiniz.</div>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={seedFromLive} disabled={seeding} data-testid="empty-seed-live" className="bg-gold text-ink-700 rounded-full px-6 h-12 text-sm font-bold hover:bg-gold-400 disabled:opacity-60 inline-flex items-center gap-2">
              ⚡ {seeding ? 'Yükleniyor…' : 'Sitedeki Aktif Menüyü Yükle'}
            </button>
            <button onClick={() => addChild(null)} data-testid="empty-add" className="border border-ink-200 text-ink-500 rounded-full px-6 h-12 text-sm font-semibold hover:bg-cream-200">
              Sıfırdan Ekle
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {flat.map((row) => (
            <Row key={row.id} row={row} pages={pages} onAddChild={addChild} onUpdate={updateItem} onDelete={deleteItem} onMove={move} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row(props) {
  const { row, pages, onAddChild, onUpdate, onDelete, onMove } = props;
  const node = row;
  const depth = row._depth;
  const [label, setLabel] = useState(node.label);
  const [url, setUrl] = useState(node.url);
  const [openTab, setOpenTab] = useState(!!node.open_in_new_tab);

  useEffect(() => {
    setLabel(node.label);
    setUrl(node.url);
    setOpenTab(!!node.open_in_new_tab);
  }, [node.id, node.label, node.url, node.open_in_new_tab]);

  const canGoDeeper = depth < 2;

  const commit = () => {
    if (label !== node.label || url !== node.url || openTab !== node.open_in_new_tab) {
      onUpdate(node.id, { label: label, url: url, open_in_new_tab: openTab });
    }
  };

  const pickPage = (slug) => {
    if (!slug) return;
    const v = '/sayfa/' + slug;
    setUrl(v);
    onUpdate(node.id, { label: label, url: v, open_in_new_tab: openTab });
  };

  return (
    <div className="bg-white rounded-2xl border border-ink-100 p-3 md:p-4 flex items-center gap-3 flex-wrap md:flex-nowrap"
         style={{ marginLeft: depth * 28 }}
         data-testid={'menu-row-' + node.id}>
      <span className="text-xs text-ink-400 tabular w-8 text-right">L{depth + 1}</span>
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={commit}
        data-testid={'menu-label-' + node.id}
        className="min-w-0 flex-1 md:flex-none md:w-56 h-10 px-3 rounded-lg border border-ink-100 text-sm font-medium text-ink-600 focus:outline-none focus:border-gold"
        placeholder="Menü etiketi"
      />
      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={commit}
        data-testid={'menu-url-' + node.id}
        className="flex-1 min-w-[200px] h-10 px-3 rounded-lg border border-ink-100 text-sm text-ink-500 focus:outline-none focus:border-gold"
        placeholder="/kurumsal veya https://..."
      />
      {pages.length > 0 && (
        <select
          onChange={(e) => pickPage(e.target.value)}
          data-testid={'menu-page-picker-' + node.id}
          className="h-10 px-2 rounded-lg border border-ink-100 text-xs text-ink-500 bg-white"
          value=""
        >
          <option value="">Sayfa seç...</option>
          {pages.map((p) => (
            <option key={p.id} value={p.slug}>{p.title}</option>
          ))}
        </select>
      )}
      <label className="flex items-center gap-1.5 text-xs text-ink-500">
        <input type="checkbox" checked={openTab} onChange={(e) => { setOpenTab(e.target.checked); onUpdate(node.id, { label: label, url: url, open_in_new_tab: e.target.checked }); }} />
        Yeni sekme
      </label>
      <div className="flex items-center gap-1 ml-auto">
        <button onClick={() => onMove(node.id, 'up')} className="w-8 h-8 grid place-items-center rounded-lg border border-ink-100 text-ink-400 hover:text-ink-600 hover:border-ink-300" title="Yukarı">↑</button>
        <button onClick={() => onMove(node.id, 'down')} className="w-8 h-8 grid place-items-center rounded-lg border border-ink-100 text-ink-400 hover:text-ink-600 hover:border-ink-300" title="Aşağı">↓</button>
        {canGoDeeper && (
          <button onClick={() => onAddChild(node.id)} data-testid={'add-child-' + node.id} className="h-8 px-2.5 rounded-lg border border-ink-100 text-ink-500 hover:border-gold hover:text-ink-600 text-xs font-medium" title="Alt menü ekle">
            + Alt
          </button>
        )}
        <button onClick={() => onDelete(node.id)} data-testid={'delete-' + node.id} className="w-8 h-8 grid place-items-center rounded-lg border border-red-100 text-red-400 hover:bg-red-50" title="Sil">
          ✕
        </button>
      </div>
    </div>
  );
}

function sortBySortOrder(a, b) {
  return (a.sort_order || 0) - (b.sort_order || 0);
}

function buildTree(items, parentId) {
  const roots = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.parent_id === parentId) {
      const clone = Object.assign({}, it);
      clone.children = buildTree(items, it.id);
      roots.push(clone);
    }
  }
  roots.sort(sortBySortOrder);
  return roots;
}

function flatten(nodes, depth) {
  const out = [];
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    out.push(Object.assign({}, n, { _depth: depth }));
    if (n.children && n.children.length > 0) {
      const kids = flatten(n.children, depth + 1);
      for (let k = 0; k < kids.length; k++) out.push(kids[k]);
    }
  }
  return out;
}
